import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/axios";
import { FaBuilding, FaCheck, FaTimes, FaArrowLeft, FaRedo, FaSpinner } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./ModifierRanch.css";

export default function ModifierRanch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nom: "",
    code: "",
    adresse: "",
    localisation: "",
    telephone: "",
    email: "",
    contact_personne: "",
    description: "",
    actif: true,
  });
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    nom: false,
  });

  // Fonction réutilisable pour charger le ranch
  const fetchRanch = async () => {
    try {
      const response = await api.get(`/api/ranches/${id}/`);
      
      // Utiliser structuredClone pour préserver les types non-sérialisables
      const clonedData = typeof structuredClone !== 'undefined' 
        ? structuredClone(response.data)
        : JSON.parse(JSON.stringify(response.data));
      
      setFormData(response.data);
      setInitialData(clonedData);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement du ranch:", error);
      
      // Créer un message d'erreur détaillé
      let errorMessage = "Impossible de charger le ranch.";
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Le ranch demandé n'existe pas.";
        } else if (error.response.status === 403) {
          errorMessage = "Vous n'avez pas la permission d'accéder à ce ranch.";
        } else if (error.response.status >= 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRanch();
  }, [id]);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation email
  const validateEmail = (email) => {
    if (!email || email.trim() === "") {
      return ""; // Pas d'erreur si le champ est vide (email optionnel)
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Veuillez entrer une adresse email valide (ex: john.doe@example.com)";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "nom") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    
    setFormData({
      ...formData,
      [name]: fieldValue,
    });

    // Validation email en temps réel
    if (name === "email") {
      const error = validateEmail(fieldValue);
      setEmailError(error);
    }

    // Validation en temps réel si le champ a été touché
    if ((name === "nom") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, fieldValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider l'email quand on quitte le champ
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
    
    // Valider le champ obligatoire
    if (name === "nom") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const hasChanges = () => {
    if (!initialData) return false;
    return (
      formData.nom !== initialData.nom ||
      formData.code !== initialData.code ||
      formData.adresse !== initialData.adresse ||
      formData.localisation !== initialData.localisation ||
      formData.telephone !== initialData.telephone ||
      formData.email !== initialData.email ||
      formData.contact_personne !== initialData.contact_personne ||
      formData.description !== initialData.description ||
      formData.actif !== initialData.actif
    );
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchRanch();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      nom: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      nom: validateField("nom", formData.nom),
    };
    setFieldErrors(newFieldErrors);
    
    // Valider l'email avant d'ouvrir le modal
    const emailValidationError = validateEmail(formData.email);
    setEmailError(emailValidationError);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = newFieldErrors.nom || emailValidationError;
    
    // Ne pas ouvrir le modal s'il y a des erreurs
    if (hasErrors) {
      return;
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.put(`/api/ranches/${id}/`, formData);
      setShowConfirmModal(false);
      toast.success("Ranch modifié avec succès !");
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/ranches");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className="modifier-ranch-page">
        <div className="loading-container">
          <p className="loading-text">Chargement du ranch...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modifier-ranch-page">
        <div className="title-badge">
          <FaBuilding className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <div className="actions">
            <button
              type="button"
              className="save-but"
              onClick={handleRetry}
            >
              <FaRedo />
              <span>Réessayer</span>
            </button>
            <button
              type="button"
              className="cancel-but"
              onClick={() => navigate("/ranches")}
            >
              <FaArrowLeft />
              <span>Retour</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-ranch-page">
      <div className="title-badge">
        <FaBuilding className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER LE RANCH {formData.nom || id}</span>
      </div>

      <form onSubmit={handleSubmit} className="ranch-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              Nom du ranch <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t.ranchNameLabel}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              required
              maxLength={191}
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="code">{t.ranchCode}</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code || ""}
              onChange={handleChange}
              placeholder={t.ranchCodeOptional}
              maxLength={50}
            />
          </div>

          <div className="form full-width">
            <label htmlFor="adresse">{t.address}</label>
            <input
              type="text"
              id="adresse"
              name="adresse"
              value={formData.adresse || ""}
              onChange={handleChange}
              placeholder={t.fullAddress}
            />
          </div>

          <div className="form">
            <label htmlFor="localisation">{t.location}</label>
            <input
              type="text"
              id="localisation"
              name="localisation"
              value={formData.localisation || ""}
              onChange={handleChange}
              placeholder={t.cityRegion}
            />
          </div>

          <div className="form">
            <label htmlFor="telephone">{t.phone}</label>
            <input
              type="text"
              id="telephone"
              name="telephone"
              value={formData.telephone || ""}
              onChange={handleChange}
              placeholder={t.phone}
            />
          </div>

          <div className="form">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t.emailPlaceholder}
              className={emailError ? "input-error" : ""}
            />
            {emailError && (
              <span className="field-error-message">{emailError}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="contact_personne">{t.contactPerson}</label>
            <input
              type="text"
              id="contact_personne"
              name="contact_personne"
              value={formData.contact_personne || ""}
              onChange={handleChange}
              placeholder={t.contactPersonName}
            />
          </div>

          <div className="form full-width">
            <label htmlFor="description">{t.description}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              placeholder={t.description}
              rows="4"
            />
          </div>

          <div className="form">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="actif"
                name="actif"
                checked={formData.actif}
                onChange={handleChange}
              />
              <span>Ranch actif</span>
            </label>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spinning" />
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <FaCheck />
                <span>Enregistrer</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/ranches")}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification du ranch</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer la modification :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom :</span>
                  <span className="summary-value">{formData.nom || "Non renseigné"}</span>
                </div>
                {formData.code && (
                  <div className="summary-row">
                    <span className="summary-label">Code :</span>
                    <span className="summary-value">{formData.code}</span>
                  </div>
                )}
                {formData.adresse && (
                  <div className="summary-row">
                    <span className="summary-label">Adresse :</span>
                    <span className="summary-value">{formData.adresse}</span>
                  </div>
                )}
                {formData.localisation && (
                  <div className="summary-row">
                    <span className="summary-label">Localisation :</span>
                    <span className="summary-value">{formData.localisation}</span>
                  </div>
                )}
                {formData.telephone && (
                  <div className="summary-row">
                    <span className="summary-label">Téléphone :</span>
                    <span className="summary-value">{formData.telephone}</span>
                  </div>
                )}
                {formData.email && (
                  <div className="summary-row">
                    <span className="summary-label">Email :</span>
                    <span className="summary-value">{formData.email}</span>
                  </div>
                )}
                {formData.contact_personne && (
                  <div className="summary-row">
                    <span className="summary-label">Personne de contact :</span>
                    <span className="summary-value">{formData.contact_personne}</span>
                  </div>
                )}
                {formData.description && (
                  <div className="summary-row">
                    <span className="summary-label">Description :</span>
                    <span className="summary-value">{formData.description}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Statut :</span>
                  <span className="summary-value">{formData.actif ? "Actif" : "Inactif"}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={isSubmitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={isSubmitting}
              >
                <FaCheck />
                <span>{isSubmitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

